import type { DataChoice, Localization } from '../types'

import React from 'react'
import { AlertTriangleIcon, CheckIcon, ChevronsUpDownIcon } from 'lucide-react'

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	InputBase,
	InputBaseAdornment,
	InputBaseControl,
	InputBaseFlexWrapper,
} from '@/shared/components/ui/input-base'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'

type FieldSelectProps = Omit<
	React.ComponentPropsWithoutRef<typeof InputBase>,
	'onChange'
> & {
	ref?: React.Ref<HTMLButtonElement>
	locale?: Localization
	name: string
	placeholder: string
	initialOpen?: boolean
	className?: string
	invalid?: boolean | string
	disabled?: boolean
	value?: string
	onChange(value: string): void
	options: DataChoice[]
	getPrefixIcon?: (value: string) => React.ReactNode
	onCloseAutoFocus?: (e: Event) => void
}
export default function Combobox({
	ref,
	className,
	name,
	invalid,
	options,
	value,
	onChange,
	disabled,
	getPrefixIcon,
	placeholder,
	initialOpen,
	locale,
	onCloseAutoFocus,
	...props
}: FieldSelectProps) {
	const [open, setOpen] = React.useState(initialOpen)
	const selectedValue = React.useMemo(
		() => options.find((option) => option.value === value),
		[value, options]
	)
	const isWrongValue = React.useMemo(
		() => Boolean(value && !selectedValue),
		[value, selectedValue]
	)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				ref={ref}
				asChild
				className={className}
				name={name}
				role='combobox'
				aria-expanded={open}
				aria-invalid={!!invalid || isWrongValue}
				disabled={disabled}
			>
				<InputBase asChild {...props} data-testid='combobox-input-base'>
					<button type='button' disabled={disabled}>
						<InputBaseControl>
							<InputBaseFlexWrapper className='truncate'>
								{selectedValue ? (
									<div className='flex items-center gap-2 truncate'>
										{getPrefixIcon?.(selectedValue.value)}
										<Tooltip>
											<TooltipTrigger asChild>
												<span className='truncate'>{selectedValue.label}</span>
											</TooltipTrigger>
											<TooltipContent>{selectedValue.label}</TooltipContent>
										</Tooltip>
									</div>
								) : isWrongValue ? (
									<div className='flex items-center gap-2 truncate'>
										<AlertTriangleIcon className='size-4 text-destructive' />
										<span className='truncate text-destructive'>{value}</span>
									</div>
								) : (
									<span className='truncate text-placeholder'>
										{placeholder}
									</span>
								)}
							</InputBaseFlexWrapper>
						</InputBaseControl>
						<InputBaseAdornment>
							<ChevronsUpDownIcon className='size-4 shrink-0 opacity-50' />
						</InputBaseAdornment>
					</button>
				</InputBase>
			</PopoverTrigger>
			<PopoverContent
				onCloseAutoFocus={onCloseAutoFocus}
				align='start'
				className='w-auto min-w-(--radix-popover-trigger-width) p-0'
			>
				<Command>
					<CommandInput
						data-testid='combobox-content-search-input'
						placeholder={locale?.getSearchPlaceholder?.() || 'Search...'}
					/>
					<CommandList>
						<CommandEmpty>
							{locale?.getNoOptionsMessage() || 'No options found.'}
						</CommandEmpty>
						<CommandGroup>
							{options.map((option) => (
								<CommandItem
									key={option.value}
									value={option.value}
									keywords={[option.label]}
									onSelect={(currentValue) => {
										onChange(currentValue)
										setOpen(false)
									}}
								>
									{getPrefixIcon?.(option.value)}
									{option.label}
									<CheckIcon
										className={cn(
											'ml-auto',
											value === option.value ? 'opacity-100' : 'opacity-0'
										)}
									/>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}
